import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SyncIcon from '@mui/icons-material/Sync';
import VisibilityIcon from '@mui/icons-material/Visibility';
import React, { Fragment, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';

import { fetchAccountManagers } from '../../services/account.service';
import { deleteAccountUser } from '../../services/accountUser.service';
import {
    customTableStyles,
    customTableStylesAccountView,
    dateTimeFormat,
} from '../../utils';
import NotifyAccount from '../account/notifyAccountManager.component';
import ViewACM from '../accountManager/acmView.component';
import EditAccountUser from '../accountUser/accountUserEdit.component';
import DeleteConfirmation from '../deleteConfirmation.component';
import MessageContainer from '../message.component';
import NotifyConfirmation from '../notifyConfirmation.component';
import WithLogin from '../withLogin.component';

const AccountManagerList = props => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [openEditForm, setOpenEditForm] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showNotifyConfirmation, setShowNotifyConfirmation] = useState(false);
    const [openNotifyForm, setOpenNotifyForm] = useState(false);
    const [accountPersonId, setAccountPersonId] = useState(false);

    const [message, setMessage] = useState({});
    const [openViewForm, setOpenViewForm] = useState(false);

    const onEdit = row => {
        setSelectedRow(row);
        setOpenEditForm(true);
    };

    const onView = row => {
        setOpenViewForm(true);
        setSelectedRow(row);
    };

    const onDelete = async row => {
        setShowDeleteConfirmation(true);
        setSelectedRow(row);
    };

    const onNotify = row => {
        setAccountPersonId(row.account_person_id);
        setShowNotifyConfirmation(true);
        setSelectedRow(row);
    };

    const notifyAccountUser = row => {
        setOpenNotifyForm(true);
        setSelectedRow(row);
    };

    const reloadDataTable = (toPage, perPage) => {
        getAccountManagers(
            toPage,
            perPage,
            props.acmSearchText,
            props.accountId,
        );

        if (toPage) {
            setPage(toPage);
        }
    };

    const DeleteAccountUser = async deleteID => {
        setMessage({});
        try {
            const resp = await deleteAccountUser(deleteID);
            reloadDataTable(page);
            setMessage({
                type: 'Success',
                message: `${resp.detail}`,
            });
        } catch (error) {
            if (
                error.response.status === 400 ||
                error.response.status === 404
            ) {
                setMessage({
                    type: 'Error',
                    message: `${error.response.data.detail}`,
                });
            }
        }
        setShowDeleteConfirmation(false);
    };

    const columns = [
        {
            name: 'id',
            cell: row => row.id,
            width: '7%',
        },
        {
            name: 'Account',
            selector: row => row.account_details.name,
            width: '10%',
        },
        {
            name: 'Name',
            selector: row => `${row.first_name} ${row.last_name}`,
            width: '10%',
        },
        {
            name: 'Email',
            selector: row => row.email,
            width: '15%',
        },

        {
            name: 'Primary',
            selector: row => (row.is_primary ? 'Yes' : 'No'),
            width: '7%',
        },
        {
            name: 'Active',
            selector: row => (row.is_active ? 'Yes' : 'No'),
            width: '7%',
        },
        {
            name: 'Created at',
            selector: row => dateTimeFormat(row.created_at),
            width: '12%',
        },
        {
            key: 'action',
            text: 'Action',
            name: 'Action',
            className: 'action',
            align: 'left',
            sortable: false,
            cell: row => {
                return (
                    <Fragment>
                        <span className="table-action-icon">
                            <EditIcon
                                className="icon-color"
                                onClick={() => {
                                    onEdit(row);
                                }}
                            />
                        </span>
                        &nbsp;
                        <span className="table-action-icon">
                            <VisibilityIcon
                                className="icon-color"
                                onClick={() => {
                                    onView(row);
                                }}
                            />
                        </span>
                        &nbsp;
                        {!row.is_primary && (
                            <DeleteIcon
                                color="red"
                                className="table-action-icon red"
                                onClick={() => onDelete(row)}
                            />
                        )}
                        &nbsp;
                        {!row.is_activated_by_user && (
                            <Link
                                onClick={() => {
                                    onNotify(row);
                                }}
                                className="table-action-icon">
                                <NotificationsNoneIcon className="icon-color" />
                            </Link>
                        )}
                    </Fragment>
                );
            },
        },
    ];

    const getAccountManagers = async (
        toPage,
        toPerPage,
        searchText,
        accountId,
    ) => {
        setLoading(true);
        toPage = toPage ? toPage : page;
        toPerPage = toPerPage ? toPerPage : perPage;
        try {
            setMessage({});
            const response = await fetchAccountManagers(
                toPage,
                toPerPage,
                searchText,
                accountId,
            );
            setData(response.items);
            setTotalRows(response.total);
            Promise.resolve();
        } catch (error) {
            setMessage({
                message: 'An error has occurrded during data loading.',
            });
            Promise.reject();
        }
        setLoading(false);
    };

    useEffect(() => {
        getAccountManagers(page, perPage, props.acmSearchText, props.accountId);
    }, [props.accountId, props.acmSearchText, props.reloadData]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePageChange = page => {
        setPage(page);
        getAccountManagers(page);
    };

    const handlePerRowsChange = async (newPerPage, toPage) => {
        setLoading(true);
        setMessage({});
        await getAccountManagers(toPage, newPerPage);
        setPerPage(newPerPage);
        setLoading(false);
    };

    return (
        <>
            {openEditForm && (
                <EditAccountUser
                    open={openEditForm}
                    setEditForm={setOpenEditForm}
                    selectedRow={selectedRow}
                    reloadDataTable={reloadDataTable}
                    accountPersonId={selectedRow.account_person_id}
                />
            )}
            {openViewForm && (
                <ViewACM
                    open={openViewForm}
                    setEditForm={setOpenViewForm}
                    selectedRow={selectedRow}
                    reloadDataTable={reloadDataTable}
                />
            )}
            {openNotifyForm && (
                <NotifyAccount
                    open={openNotifyForm}
                    setNotifyForm={setOpenNotifyForm}
                    accountPersonId={accountPersonId}
                    reloadDataTable={reloadDataTable}
                />
            )}
            <span
                color="primary"
                className="float-end"
                style={{ marginRight: '10px' }}
                onClick={() => reloadDataTable()}>
                <SyncIcon />
            </span>
            <div>
                <div className="new-line"></div>
                {message && <MessageContainer message={message} />}
                <div className="accounts-list-table">
                    <DataTable
                        width="100%"
                        columns={columns}
                        customStyles={
                            props.accountId
                                ? customTableStylesAccountView
                                : customTableStyles
                        }
                        data={data}
                        progressPending={loading}
                        pagination
                        paginationServer
                        paginationDefaultPage={page}
                        paginationResetDefaultPage={true}
                        paginationTotalRows={totalRows}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onChangePage={handlePageChange}
                    />
                </div>
                <DeleteConfirmation
                    showModal={showDeleteConfirmation}
                    hideModal={() => setShowDeleteConfirmation(false)}
                    confirmModal={DeleteAccountUser}
                    deleteId={selectedRow.account_person_id}
                    message="Are you sure you want to delete it"
                />
                {showNotifyConfirmation && (
                    <NotifyConfirmation
                        showModal={showNotifyConfirmation}
                        hideModal={() => setShowNotifyConfirmation(false)}
                        confirmModal={notifyAccountUser}
                        buttonName="Send"
                        notifyAccountId={selectedRow}
                        message={`Do you want to resend welcome email with login credential to ${selectedRow.first_name} ${selectedRow.last_name} (${selectedRow.email})`}
                    />
                )}
            </div>
        </>
    );
};

export default WithLogin(AccountManagerList);
